import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyleWide, FormRow, DetailField, fmtMoney, fmtQty } from './ProcurementShared';

export default function ProcurementReceiving() {
  const [tab, setTab] = useState('receivings');
  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📥 Receiving & Returns</h2>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button style={tab === 'receivings' ? btnPrimary : btnSecondary} onClick={() => setTab('receivings')}>Receivings</button>
        <button style={tab === 'returns' ? btnPrimary : btnSecondary} onClick={() => setTab('returns')}>Returns</button>
      </div>
      {tab === 'receivings' ? <ReceivingsTab /> : <ReturnsTab />}
    </div>
  );
}

function useConfirmedPOs(companyId) {
  const [pos, setPos] = useState([]);
  const fetchPos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/procurement/purchase-orders?per_page=200&receiving_status=not_received`, { headers: getAuthHeaders() });
      const json = await res.json();
      let data = Array.isArray(json.data) ? json.data : [];
      const res2 = await fetch(`${API}/companies/${companyId}/procurement/purchase-orders?per_page=200&receiving_status=partially_received`, { headers: getAuthHeaders() });
      const json2 = await res2.json();
      data = data.concat(Array.isArray(json2.data) ? json2.data : []);
      setPos(data.filter(po => ['confirmed', 'partially_received'].includes(po.status)));
    } catch (e) { console.error(e); }
  }, [companyId]);
  useEffect(() => { fetchPos(); }, [fetchPos]);
  return pos;
}

function useWarehouses(companyId) {
  const [warehouses, setWarehouses] = useState([]);
  useEffect(() => {
    fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(j => setWarehouses(Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []))).catch(() => {});
  }, [companyId]);
  return warehouses;
}

function ReceivingsTab() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const pos = useConfirmedPOs(companyId);
  const warehouses = useWarehouses(companyId);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/procurement/receivings?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const viewItem = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/receivings/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const confirmItem = async (id) => {
    const wh = warehouses[0];
    if (!wh) { alert('No warehouse found — create one in Inventory > Warehouses first.'); return; }
    const whId = window.prompt('Warehouse ID to receive into:', wh.id);
    if (!whId) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/receivings/${id}/confirm`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ warehouse_id: Number(whId) }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Confirm failed'); return; }
    fetchList(); if (viewing) viewItem(id);
  };

  const reverseItem = async (id) => {
    const reason = window.prompt('Reversal reason (required):');
    if (!reason) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/receivings/${id}/reverse`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Reverse failed'); return; }
    fetchList(); if (viewing) viewItem(id);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option><option value="confirmed">Confirmed</option><option value="reversed">Reversed</option>
        </select>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Receiving</button>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Receiving #', 'PO #', 'Supplier', 'Date', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No receivings</td></tr>}
              {list.map(r => (
                <tr key={r.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}><a href="#view" onClick={e => { e.preventDefault(); viewItem(r.id); }} style={{ color: '#3b82f6' }}>{r.receiving_number}</a></td>
                  <td style={tdStyle}>{r.po?.po_number || '—'}</td>
                  <td style={tdStyle}>{r.supplier?.name || '—'}</td>
                  <td style={tdStyle}>{r.receiving_date ? new Date(r.receiving_date).toLocaleDateString() : '—'}</td>
                  <td style={tdStyle}><Badge value={r.status} /></td>
                  <td style={tdStyle}>
                    {r.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => confirmItem(r.id)}>Confirm</button>}
                    {r.status === 'confirmed' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => reverseItem(r.id)}>Reverse</button>}
                    <button style={btnSmall} onClick={() => viewItem(r.id)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <ReceivingModal companyId={companyId} pos={pos} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {viewing && (
        <div style={overlayStyle} onClick={() => setViewing(null)}>
          <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{viewing.receiving_number} <Badge value={viewing.status} /></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
              <DetailField label="PO #" value={viewing.po?.po_number} />
              <DetailField label="Supplier" value={viewing.supplier?.name} />
              <DetailField label="Receiving Date" value={viewing.receiving_date ? new Date(viewing.receiving_date).toLocaleDateString() : '—'} />
              <DetailField label="Delivery Ref" value={viewing.delivery_reference} />
            </div>
            <table style={tableStyle}>
              <thead><tr>{['Description', 'Ordered', 'Prev. Received', 'Qty Received', 'Unit Cost'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(viewing.lines || []).map(l => (
                  <tr key={l.id}><td style={tdStyle}>{l.description}</td><td style={tdStyle}>{fmtQty(l.ordered_qty)}</td><td style={tdStyle}>{fmtQty(l.previously_received_qty)}</td><td style={tdStyle}>{fmtQty(l.qty_received)}</td><td style={tdStyle}>{fmtMoney(l.unit_cost)}</td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button style={btnSecondary} onClick={() => setViewing(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceivingModal({ companyId, pos, onClose, onSaved }) {
  const [poId, setPoId] = useState('');
  const [poDetail, setPoDetail] = useState(null);
  const [lines, setLines] = useState([]);
  const [deliveryReference, setDeliveryReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!poId) { setPoDetail(null); setLines([]); return; }
    fetch(`${API}/companies/${companyId}/procurement/purchase-orders/${poId}`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(po => {
        setPoDetail(po);
        setLines((po.lines || []).map(l => ({ po_line_id: l.id, description: l.description, remaining: Number(l.quantity) - Number(l.received_qty || 0), qty_received: 0 })));
      });
  }, [poId, companyId]);

  const updateQty = (i, val) => {
    const next = [...lines]; next[i] = { ...next[i], qty_received: val }; setLines(next);
  };

  const save = async () => {
    if (!poId) { alert('Select a PO'); return; }
    const validLines = lines.filter(l => Number(l.qty_received) > 0).map(l => ({ po_line_id: l.po_line_id, qty_received: Number(l.qty_received) }));
    if (validLines.length === 0) { alert('Enter quantity received for at least one line'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/procurement/receivings`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_id: Number(poId), delivery_reference: deliveryReference, lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Receiving</h3>
        <FormRow label="Purchase Order *">
          <select style={inputStyle} value={poId} onChange={e => setPoId(e.target.value)}>
            <option value="">Select PO...</option>
            {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.supplier?.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Delivery Reference"><input style={inputStyle} value={deliveryReference} onChange={e => setDeliveryReference(e.target.value)} /></FormRow>

        {poDetail && (
          <>
            <h4>Lines</h4>
            <table style={tableStyle}>
              <thead><tr>{['Description', 'Remaining', 'Qty Received'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{l.description}</td>
                    <td style={tdStyle}>{fmtQty(l.remaining)}</td>
                    <td style={{ ...tdStyle, width: 100 }}><input type="number" style={inputStyle} value={l.qty_received} onChange={e => updateQty(i, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function ReturnsTab() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const warehouses = useWarehouses(companyId);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/procurement/returns?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const viewItem = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/returns/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const confirmItem = async (id) => {
    const wh = warehouses[0];
    const whId = window.prompt('Warehouse ID to return stock from:', wh ? wh.id : '');
    if (!whId) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/returns/${id}/confirm`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ warehouse_id: Number(whId) }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Confirm failed'); return; }
    fetchList(); if (viewing) viewItem(id);
  };

  const doSimpleAction = async (id, action) => {
    let extra = {};
    if (action === 'cancel') {
      const reason = window.prompt('Cancellation reason (required):');
      if (!reason) return;
      extra = { reason };
    }
    const res = await fetch(`${API}/companies/${companyId}/procurement/returns/${id}/${action}`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(extra),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Action failed'); return; }
    fetchList(); if (viewing) viewItem(id);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 180 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
        </select>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Return</button>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Return #', 'PO #', 'Supplier', 'Total Value', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No returns</td></tr>}
              {list.map(r => (
                <tr key={r.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}><a href="#view" onClick={e => { e.preventDefault(); viewItem(r.id); }} style={{ color: '#3b82f6' }}>{r.return_number}</a></td>
                  <td style={tdStyle}>{r.po?.po_number || '—'}</td>
                  <td style={tdStyle}>{r.supplier?.name || '—'}</td>
                  <td style={tdStyle}>{fmtMoney(r.total_value)}</td>
                  <td style={tdStyle}><Badge value={r.status} /></td>
                  <td style={tdStyle}>
                    {r.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => confirmItem(r.id)}>Confirm</button>}
                    {r.status === 'draft' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => doSimpleAction(r.id, 'cancel')}>Cancel</button>}
                    {r.status === 'confirmed' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => doSimpleAction(r.id, 'complete')}>Complete</button>}
                    <button style={btnSmall} onClick={() => viewItem(r.id)}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <ReturnModal companyId={companyId} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
      {viewing && (
        <div style={overlayStyle} onClick={() => setViewing(null)}>
          <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{viewing.return_number} <Badge value={viewing.status} /></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
              <DetailField label="PO #" value={viewing.po?.po_number} />
              <DetailField label="Supplier" value={viewing.supplier?.name} />
              <DetailField label="Reason" value={viewing.reason} />
            </div>
            <table style={tableStyle}>
              <thead><tr>{['Description', 'Qty', 'Unit Cost', 'Return Value'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {(viewing.lines || []).map(l => (
                  <tr key={l.id}><td style={tdStyle}>{l.description}</td><td style={tdStyle}>{fmtQty(l.quantity)}</td><td style={tdStyle}>{fmtMoney(l.unit_cost)}</td><td style={tdStyle}>{fmtMoney(l.return_value)}</td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button style={btnSecondary} onClick={() => setViewing(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReturnModal({ companyId, onClose, onSaved }) {
  const [poId, setPoId] = useState('');
  const [pos, setPos] = useState([]);
  const [poDetail, setPoDetail] = useState(null);
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/procurement/purchase-orders?per_page=200`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(j => setPos((Array.isArray(j.data) ? j.data : []).filter(po => Number(po.total_received_qty || 0) >= 0)));
  }, [companyId]);

  useEffect(() => {
    if (!poId) { setPoDetail(null); setLines([]); return; }
    fetch(`${API}/companies/${companyId}/procurement/purchase-orders/${poId}`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(po => {
        setPoDetail(po);
        setLines((po.lines || []).filter(l => Number(l.received_qty) > 0).map(l => ({ po_line_id: l.id, description: l.description, received: Number(l.received_qty), unit_cost: l.unit_cost, quantity: 0 })));
      });
  }, [poId, companyId]);

  const updateLine = (i, field, val) => { const next = [...lines]; next[i] = { ...next[i], [field]: val }; setLines(next); };

  const save = async () => {
    if (!poId) { alert('Select a PO'); return; }
    const validLines = lines.filter(l => Number(l.quantity) > 0).map(l => ({ po_line_id: l.po_line_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) }));
    if (validLines.length === 0) { alert('Enter return quantity for at least one line'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/procurement/returns`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_id: Number(poId), reason, lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyleWide} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Return</h3>
        <FormRow label="Purchase Order *">
          <select style={inputStyle} value={poId} onChange={e => setPoId(e.target.value)}>
            <option value="">Select PO...</option>
            {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.supplier?.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Reason"><input style={inputStyle} value={reason} onChange={e => setReason(e.target.value)} /></FormRow>

        {poDetail && (
          <>
            <h4>Lines (only lines with received quantity shown)</h4>
            <table style={tableStyle}>
              <thead><tr>{['Description', 'Received', 'Unit Cost', 'Return Qty'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {lines.length === 0 && <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No received lines on this PO</td></tr>}
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{l.description}</td>
                    <td style={tdStyle}>{fmtQty(l.received)}</td>
                    <td style={tdStyle}>{fmtMoney(l.unit_cost)}</td>
                    <td style={{ ...tdStyle, width: 100 }}><input type="number" style={inputStyle} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
